import { useId } from "react";
import { motion } from "framer-motion";

const INDIGO = "#6366f1";
const BLUE   = "#3b82f6";
const ACCENT = "#8b5cf6"; // used by SectionPreloader

/* Deterministic particle layout — avoids random on every render */
const PARTICLES = Array.from({ length: 16 }, (_, i) => ({
  left: `${(i * 6.3 + 2) % 96}%`,
  delay: `${(i * 0.43) % 5}s`,
  dur: `${3.5 + (i * 0.9) % 4.5}s`,
  size: i % 4 === 0 ? 3 : 2,
}));

const EASE = [0.22, 1, 0.36, 1];

/* ═══════════════════════════════════════════════════════════
   MAIN PRELOADER — full-screen app startup
   ═══════════════════════════════════════════════════════════ */
export default function Preloader() {
  const uid = useId().replace(/:/g, "");

  return (
    <motion.div
      key="preloader"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.04, filter: "blur(18px)" }}
      transition={{ duration: 0.55, ease: EASE }}
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: "#09090b",
        fontFamily: "'DM Sans', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* ── Ambient background ── */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {/* Gradient orbs */}
        <div className="sl-pl-orb" style={{
          position: "absolute", width: 500, height: 500, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(99,102,241,.07) 0%, transparent 70%)",
          filter: "blur(80px)", top: "35%", left: "45%",
          transform: "translate(-50%, -50%)",
        }} />
        <div className="sl-pl-orb2" style={{
          position: "absolute", width: 380, height: 380, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(59,130,246,.05) 0%, transparent 70%)",
          filter: "blur(60px)", top: "55%", left: "55%",
          transform: "translate(-50%, -50%)",
        }} />
        {/* Subtle dot grid */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.012,
          backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }} />
        {/* Floating particles */}
        {PARTICLES.map((p, i) => (
          <div key={i} className="sl-pl-particle" style={{
            left: p.left, animationDelay: p.delay, animationDuration: p.dur,
            width: p.size, height: p.size,
          }} />
        ))}
      </div>

      {/* ── Logo assembly ── */}
      <div style={{
        position: "relative", display: "flex",
        alignItems: "center", justifyContent: "center",
        width: 140, height: 140,
      }}>
        {/* Pulsing outer ring */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.12, 0.3, 0.12] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute", inset: -12, borderRadius: "50%",
            border: `1px solid ${INDIGO}35`,
          }}
        />
        {/* Pulsing inner ring (counter-phase) */}
        <motion.div
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.08, 0.22, 0.08] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute", inset: -24, borderRadius: "50%",
            border: `1px solid ${BLUE}20`,
          }}
        />

        {/* Orbiting dot */}
        <div className="sl-pl-orbit" style={{
          position: "absolute", inset: -16, borderRadius: "50%",
        }}>
          <div style={{
            position: "absolute", top: -2, left: "50%", transform: "translateX(-50%)",
            width: 5, height: 5, borderRadius: "50%",
            background: INDIGO,
            boxShadow: `0 0 10px ${INDIGO}, 0 0 22px ${INDIGO}50`,
          }} />
        </div>

        {/* Logo container */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.65, ease: EASE }}
          style={{
            width: 84, height: 84, borderRadius: 24,
            position: "relative", overflow: "hidden",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 0 80px ${INDIGO}12, 0 24px 60px rgba(0,0,0,.5)`,
          }}
        >
          {/* Rotating conic border glow */}
          <div className="sl-pl-conic" style={{
            position: "absolute", inset: -1, borderRadius: "inherit",
            background: `conic-gradient(from 0deg, transparent 0%, ${INDIGO}40 20%, transparent 40%, ${BLUE}30 70%, transparent 90%)`,
          }} />
          {/* Inner fill */}
          <div style={{
            position: "absolute", inset: 1.5, borderRadius: 22,
            background: "linear-gradient(145deg, rgba(15,15,18,.97), rgba(9,9,11,.99))",
          }} />

          {/* Animated StudyLift logo SVG */}
          <svg width="42" height="42" viewBox="0 0 40 40" fill="none"
            style={{ position: "relative", zIndex: 1 }}>
            <defs>
              <linearGradient id={`${uid}g`} x1="0" y1="0" x2="40" y2="40"
                gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#60a5fa" />
                <stop offset="55%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#6366f1" />
              </linearGradient>
            </defs>
            {/* Background — fades in */}
            <motion.rect width="40" height="40" rx="11" fill={`url(#${uid}g)`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.15 }} />
            {/* Triangle — slides up into place */}
            <motion.path d="M20 8L29 24H11L20 8Z" fill="white" fillOpacity=".96"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 0.96, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4, ease: EASE }} />
            {/* Long bar — slides in from left */}
            <motion.rect x="11" y="27" width="18" height="3" rx="1.5"
              fill="white" fillOpacity=".75"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 0.75, x: 0 }}
              transition={{ duration: 0.4, delay: 0.6, ease: EASE }} />
            {/* Short bar — slides in from right */}
            <motion.rect x="14.5" y="32" width="11" height="2" rx="1"
              fill="white" fillOpacity=".38"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 0.38, x: 0 }}
              transition={{ duration: 0.4, delay: 0.75, ease: EASE }} />
          </svg>
        </motion.div>
      </div>

      {/* ── Brand name — letter-by-letter stagger ── */}
      <div style={{ marginTop: 28, display: "flex", overflow: "hidden" }}>
        {"StudyLift".split("").map((char, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 + i * 0.045, duration: 0.35, ease: EASE }}
            style={{
              fontSize: 22, fontWeight: 900, letterSpacing: "-0.5px",
              background: "linear-gradient(135deg, #f4f4f5 40%, #a1a1aa)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >{char}</motion.span>
        ))}
      </div>

      {/* ── Tagline ── */}
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.4 }}
        style={{
          marginTop: 8, fontSize: 12, fontWeight: 500,
          color: "rgba(244,244,245,.28)", letterSpacing: "0.5px",
        }}
      >
        Learn live. Rise higher.
      </motion.p>

      {/* ── Progress bar ── */}
      <motion.div
        initial={{ opacity: 0, width: 0 }}
        animate={{ opacity: 1, width: 180 }}
        transition={{ delay: 0.7, duration: 0.3 }}
        style={{
          marginTop: 28, height: 2, borderRadius: 1,
          background: "rgba(255,255,255,.06)", overflow: "hidden",
        }}
      >
        <div className="sl-pl-progress" style={{
          height: "100%", borderRadius: 1,
          background: `linear-gradient(90deg, ${INDIGO}, ${BLUE})`,
          boxShadow: `0 0 8px ${INDIGO}40`,
        }} />
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION PRELOADER — inline variant for dashboard sections
   ═══════════════════════════════════════════════════════════ */
export function SectionPreloader({ label = "Loading…", height = 280 }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, filter: "blur(4px)" }}
      transition={{ duration: 0.3 }}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: height, position: "relative", overflow: "hidden", borderRadius: 16,
      }}
    >
      <div className="sl-preload-glow" style={{
        position: "absolute", width: 240, height: 240, borderRadius: "50%",
        background: `radial-gradient(circle, ${ACCENT}10, transparent 70%)`,
        filter: "blur(50px)", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        pointerEvents: "none",
      }} />
      <div style={{ position: "relative", width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="sl-preload-orbit" style={{
          position: "absolute", inset: -5, borderRadius: "50%",
          border: "1px solid rgba(255,255,255,.04)",
        }}>
          <div style={{
            position: "absolute", top: -2, left: "50%", transform: "translateX(-50%)",
            width: 4, height: 4, borderRadius: "50%",
            background: ACCENT, boxShadow: `0 0 8px ${ACCENT}`,
          }} />
        </div>
        <motion.div
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{
            width: 40, height: 40, borderRadius: 12,
            background: "linear-gradient(145deg, rgba(255,255,255,.06), rgba(255,255,255,.02))",
            border: "1px solid rgba(255,255,255,.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative", overflow: "hidden",
            boxShadow: `0 0 20px ${ACCENT}10, 0 4px 16px rgba(0,0,0,.3)`,
          }}
        >
          <div className="sl-preload-border-glow" style={{
            position: "absolute", inset: -1, borderRadius: "inherit",
            background: `conic-gradient(from 0deg, transparent, ${ACCENT}30, transparent, ${BLUE}30, transparent)`,
            opacity: 0.5,
          }} />
          <div style={{
            position: "absolute", inset: 1, borderRadius: 11,
            background: "linear-gradient(145deg, rgba(15,15,18,.95), rgba(9,9,11,.98))",
          }} />
          <span style={{
            position: "relative", zIndex: 1, fontSize: 14, fontWeight: 900, letterSpacing: "-1px",
            background: `linear-gradient(135deg, ${ACCENT}, #60a5fa, ${BLUE})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          }}>SL</span>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.3 }}
        style={{ marginTop: 14, fontSize: 12, fontWeight: 500, color: "#52525b", letterSpacing: "0.3px" }}
      >
        {label}
      </motion.div>
      <div style={{
        marginTop: 12, width: 80, height: 2, borderRadius: 1,
        background: "rgba(255,255,255,.06)", overflow: "hidden",
      }}>
        <div className="sl-preload-progress" style={{
          height: "100%", borderRadius: 1,
          background: `linear-gradient(90deg, ${ACCENT}, ${BLUE}, ${ACCENT})`,
          backgroundSize: "200% 100%",
        }} />
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MINI SPINNER — for inline button states
   ═══════════════════════════════════════════════════════════ */
export function MiniSpinner({ size = 16, color = "#fff" }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size, borderRadius: "50%",
      border: `2px solid ${color}30`,
      borderTopColor: color,
      animation: "sl-spin .6s linear infinite",
      flexShrink: 0,
    }} />
  );
}
